import { makeStyles } from "@material-ui/core/styles";

// Field marks follow broadcast convention: blue line of scrimmage, yellow line
// to gain, an orange ball. These read on either turf.
export const REPLAY_COLORS = {
	los: "#1c7ed6",
	chains: "#d9a406",
	ball: "#c04a22",
	gain: "#2f7d4f",
	loss: "#b03030"
};

// Turf and chalk follow the theme the dialog is mounted in, so the field sits
// with the surrounding surface instead of fighting it. Anything drawn OUTSIDE
// the turf (the play's arrows, the action chain, the trails of earlier plays)
// must take its colour from the palette, not from chalk -- chalk on a light
// dialog is invisible.
const driveReplayDialogStyles = makeStyles((theme) => {
	const dark = theme.palette.type === "dark";
	const turf = dark ? "#24352a" : "#cfdcc9";
	const chalk = dark ? "#d8e4d8" : "#ffffff";
	// One outline for everything that rides on the turf -- the ball and both of
	// its labels -- so they read against chalk lines and turf bands alike.
	const outline = dark ? "#16241b" : "#3d5140";

	return {
		paper: {
			maxWidth: 1120
		},
		title: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			paddingBottom: theme.spacing(1)
		},
		titleText: {
			flexGrow: 1,
			minWidth: 0
		},
		content: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2),
			// Flex items shrink by default, so a dialog taller than the window
			// squashed the field (cutting the lower legs off a fumble) and the
			// play strip instead of scrolling. Let the dialog scroll instead.
			"& > *": {
				flexShrink: 0
			}
		},
		fieldWrapper: {
			overflowX: "auto",
			// With overflow-x set, the other axis computes to auto as well, so the
			// descender space under the inline SVG was enough to raise a stray
			// vertical scrollbar. Clipping it instead would cut the lower legs off
			// a multi-leg play, since the drawing grows with them.
			lineHeight: 0
		},
		field: {
			display: "block",
			width: "100%",
			minWidth: 620,
			height: "auto"
		},
		turf: {
			fill: turf
		},
		turfBand: {
			fill: dark ? "#1f2f25" : "#c4d3bd"
		},
		endZone: {
			fill: dark ? "#16241b" : "#9db396"
		},
		chalk: {
			stroke: chalk
		},
		yardNumber: {
			fill: chalk,
			opacity: 0.62,
			fontSize: 17
		},
		endZoneLabel: {
			fill: chalk,
			opacity: 0.55,
			fontSize: 15,
			letterSpacing: "0.2em"
		},
		ballLabel: {
			fill: chalk,
			fontSize: 13,
			fontWeight: 600,
			paintOrder: "stroke",
			stroke: outline,
			strokeWidth: 3,
			strokeLinejoin: "round"
		},
		ballCarrier: {
			fill: chalk,
			fontSize: 14,
			fontWeight: 600,
			paintOrder: "stroke",
			stroke: outline,
			strokeWidth: 3,
			strokeLinejoin: "round"
		},
		ball: {
			stroke: outline,
			strokeWidth: 2.5
		},
		// The ball and both chains slide to the next play rather than jumping,
		// which is the whole point of replaying a drive on the field.
		moves: {
			transition: "transform 900ms cubic-bezier(.32,.72,.28,1)"
		},
		// Each leg of the play draws itself out as the ball reaches it. Keyed on
		// the leg, so React remounts the path and restarts the animation -- a
		// transition would not re-run for a freshly mounted node.
		"@keyframes driveReplayShot": {
			from: {
				strokeDashoffset: "var(--shot-length)"
			},
			to: {
				strokeDashoffset: 0
			}
		},
		shot: {
			fill: "none",
			strokeWidth: 4,
			animation: "$driveReplayShot 700ms cubic-bezier(.32,.72,.28,1) both"
		},
		// Legs the ball has already covered on this play stay drawn, faded.
		shotDone: {
			fill: "none",
			strokeWidth: 4,
			opacity: 0.45
		},
		// Below the turf, on the dialog's own surface -- palette colours, not
		// chalk.
		trail: {
			stroke: theme.palette.text.secondary,
			strokeWidth: 3,
			opacity: 0.3
		},
		trailTag: {
			fill: theme.palette.text.secondary,
			opacity: 0.75,
			fontSize: 11,
			fontVariantNumeric: "tabular-nums"
		},
		legTag: {
			fontSize: 11,
			letterSpacing: "0.1em",
			fontWeight: 600
		},
		legYards: {
			fontSize: 14,
			fontWeight: 700,
			fontVariantNumeric: "tabular-nums"
		},
		// The ball changed hands: a ring opens out of it twice. transform-box
		// makes the SVG circle scale about its own centre.
		"@keyframes driveReplayTurnover": {
			from: {
				transform: "scale(1)",
				opacity: 0.9
			},
			to: {
				transform: "scale(2.6)",
				opacity: 0
			}
		},
		turnoverPulse: {
			transformBox: "fill-box",
			transformOrigin: "center",
			animation: "$driveReplayTurnover 700ms ease-out 2 both"
		},
		// The play scored: the same ring, in the scoring colour.
		scorePulse: {
			transformBox: "fill-box",
			transformOrigin: "center",
			animation: "$driveReplayTurnover 900ms ease-out 2 both"
		},
		// The points rise off the top of the ball and fade.
		"@keyframes driveReplayPoints": {
			"0%": {
				transform: "translateY(0)",
				opacity: 0
			},
			"20%": {
				opacity: 1
			},
			"100%": {
				transform: "translateY(-38px)",
				opacity: 0
			}
		},
		scorePoints: {
			fill: REPLAY_COLORS.gain,
			fontSize: 20,
			fontWeight: 700,
			paintOrder: "stroke",
			stroke: outline,
			strokeWidth: 3,
			strokeLinejoin: "round",
			animation: "$driveReplayPoints 1800ms ease-out both"
		},
		gainTag: {
			fill: REPLAY_COLORS.ball,
			fontSize: 15,
			fontWeight: 600
		},
		// HTML, not SVG text: a long play description has to wrap, and SVG text
		// does not. Left-aligned in a centred column -- centred prose of this
		// length is hard to scan.
		description: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(0.25),
			minWidth: 0,
			// A long account scrolls inside its own card rather than stretching
			// the row it shares.
			overflowY: "auto"
		},
		descriptionLead: {
			fontSize: 15,
			fontWeight: 500,
			textWrap: "pretty"
		},
		// The clauses after the first -- the fumble, the recovery, the return --
		// step in behind it.
		descriptionPart: {
			paddingLeft: theme.spacing(2),
			fontSize: 13.5,
			color: theme.palette.text.secondary,
			textWrap: "pretty",
			"&::before": {
				content: "'› '",
				opacity: 0.6
			}
		},
		"@media (prefers-reduced-motion: reduce)": {
			moves: {
				transition: "none"
			},
			shot: {
				animation: "none"
			},
			turnoverPulse: {
				animation: "none"
			},
			scorePulse: {
				animation: "none"
			},
			scorePoints: {
				animation: "none"
			}
		},
		readouts: {
			display: "grid",
			gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)",
			gap: theme.spacing(2),
			// Keyed to the dialog's own width, not the window's: at MUI's `sm`
			// the two cards stacked even in a dialog wide enough to hold them.
			"@media (max-width: 700px)": {
				gridTemplateColumns: "minmax(0, 1fr)"
			}
		},
		card: {
			border: `1px solid ${theme.palette.border.primary}`,
			borderRadius: 4,
			padding: theme.spacing(2)
		},
		chips: {
			display: "flex",
			flexWrap: "wrap",
			gap: theme.spacing(1),
			marginBottom: theme.spacing(1)
		},
		narration: {
			margin: 0
		},
		spots: {
			marginTop: theme.spacing(1),
			fontVariantNumeric: "tabular-nums",
			color: theme.palette.text.secondary
		},
		tally: {
			display: "grid",
			gridTemplateColumns: "1fr 1fr",
			gap: theme.spacing(1.5),
			alignContent: "start"
		},
		statValue: {
			fontWeight: 600,
			fontVariantNumeric: "tabular-nums"
		},
		// The play's account and the drive's player yardage share a row: one
		// column for the words, two for the yardage. Its height is fixed because
		// the account runs from one line to five depending on the play, and a row
		// that grows with it made everything below jump between plays.
		detailRow: {
			display: "grid",
			gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
			gap: theme.spacing(2),
			height: 172,
			"@media (max-width: 700px)": {
				gridTemplateColumns: "minmax(0, 1fr)",
				height: "auto"
			}
		},
		playerYards: {
			gridColumn: "span 2",
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1),
			minWidth: 0,
			"@media (max-width: 700px)": {
				gridColumn: "span 1"
			}
		},
		// Away on the left, home on the right -- the order the scoreboard uses.
		playerYardsTeams: {
			display: "grid",
			gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
			gap: theme.spacing(0, 2),
			minHeight: 0,
			overflowY: "auto"
		},
		playerYardsTeam: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(0.25),
			minWidth: 0
		},
		playerYardsRow: {
			display: "flex",
			alignItems: "baseline",
			gap: theme.spacing(1)
		},
		playerYardsName: {
			fontWeight: 600,
			whiteSpace: "nowrap"
		},
		playerYardsCategory: {
			flexGrow: 1,
			minWidth: 0,
			fontSize: 13,
			color: theme.palette.text.secondary
		},
		playerYardsValue: {
			fontWeight: 600,
			fontVariantNumeric: "tabular-nums"
		},
		strip: {
			display: "flex",
			gap: theme.spacing(0.75),
			overflowX: "auto",
			paddingBottom: theme.spacing(0.5)
		},
		transport: {
			display: "flex",
			alignItems: "center",
			flexWrap: "wrap",
			gap: theme.spacing(1)
		},
		transportPrimary: {
			borderColor: REPLAY_COLORS.ball,
			color: REPLAY_COLORS.ball,
			minWidth: 92
		},
		// MUI fades a disabled button's label almost to nothing on a light
		// surface, so Prev/Next at the ends of a drive read as empty boxes.
		transportButton: {
			"&.Mui-disabled": {
				color: theme.palette.text.disabled,
				borderColor: theme.palette.action.disabled
			}
		},
		transportSpeed: {
			marginLeft: "auto",
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1)
		},
		tick: {
			flex: "1 1 0",
			minWidth: 78,
			textTransform: "none",
			padding: theme.spacing(0.75, 1),
			lineHeight: 1.25
		},
		tickLabel: {
			flexDirection: "column",
			alignItems: "flex-start"
		},
		tickCurrent: {
			borderColor: REPLAY_COLORS.ball,
			color: REPLAY_COLORS.ball
		},
		// A tick reads top to bottom: the situation, what the play was, where
		// the ball finished.
		tickDown: {
			fontSize: 11,
			letterSpacing: "0.08em",
			textTransform: "uppercase",
			color: theme.palette.text.secondary
		},
		tickGain: {
			fontWeight: 600,
			fontSize: 15,
			textTransform: "uppercase",
			fontVariantNumeric: "tabular-nums"
		},
		tickSpot: {
			fontSize: 11,
			fontVariantNumeric: "tabular-nums",
			color: theme.palette.text.secondary
		}
	};
});

export default driveReplayDialogStyles;
